<template>
  <RevModalBase
    backdrop-background-color="hi"
    background-color="mid"
    class="rounded-t-lg md:bottom-auto md:left-1/2 md:top-1/2 md:w-640 md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-b-lg"
    :close-button-label="i18n(translations.modalCloseA11y)"
    has-padding
    :name="modalName"
    :title="
      view === 'country'
        ? i18n(translations.switchCountryTitle)
        : i18n(translations.modalTitle)
    "
    @close="resetView"
  >
    <template v-if="view === 'country'" #beforeTitle>
      <RevButtonIcon
        :aria-label="i18n(translations.switchCountryBackA11y)"
        class="absolute left-0 top-0"
        :icon="IconArrowLeft"
        variant="ghost"
        @click="resetView"
      />
    </template>

    <template #body>
      <PostalCodeForm
        v-if="view === 'postal-code'"
        @submit="handlePostalCodeSubmit"
        @switch-country="view = 'country'"
      />
      <CountrySwitchMenuSearchList v-else />
    </template>

    <template #trigger="{ open }">
      <slot name="trigger" :open>
        <span
          class="inline-flex items-center gap-4 body-2"
          data-qa="delivery-postal-code-trigger"
        >
          <IconLocationPinFilled size="12" />
          <RevLink v-if="!postalCode" @click="open">
            {{ i18n(translations.updateLabel) }}
          </RevLink>
          <FormattedMessage v-else :definition="translations.shipToLabel">
            <template #postalCode>
              <RevLink @click="open">{{ postalCode }}</RevLink>
            </template>
          </FormattedMessage>
        </span>
      </slot>
    </template>
  </RevModalBase>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevLink } from '@ds/components/Link'
import { RevModalBase, closeModal } from '@ds/components/ModalBase'
import { IconArrowLeft } from '@ds/icons/IconArrowLeft'
import { IconLocationPinFilled } from '@ds/icons/IconLocationPinFilled'

import CountrySwitchMenuSearchList from '~/scopes/navigation/components/CountrySwitch/CountrySwitchMenuSearchList.vue'
import { useDeliveryPostalCode } from '~/scopes/shipping/composables/useDeliveryPostalCode/useDeliveryPostalCode'

import translations from './DeliveryPostalCodeModal.translations'
import PostalCodeForm from './PostalCodeForm/PostalCodeForm.vue'

const props = defineProps<{
  entryPoint: 'pdp' | 'cart' | 'nav'
}>()

const i18n = useI18n()
const { postalCode } = useDeliveryPostalCode()
const { openSuccessToast } = useTheToast()
const { trackFormSubmit } = useTracking()

const view = ref<'postal-code' | 'country'>('postal-code')

const modalName = computed(
  () => `postalcode_delivery_promise_${props.entryPoint}`,
)

function handlePostalCodeSubmit() {
  closeModal(modalName.value)

  // Only the success path is reachable today. `status: 'error'`/`outcome` and
  // `delivery_promise_changed: true` need the API-validated flow (CK-6319).
  trackFormSubmit({
    zone: 'postalcode_modal',
    entryPoint: props.entryPoint,
    status: 'success',
    deliveryPromiseChanged: false,
  })
  openSuccessToast({
    title: i18n(translations.successTitle),
    content: i18n(translations.successDescription),
  })
}

function resetView() {
  view.value = 'postal-code'
}
</script>
